<script lang="ts" setup>
import { ref, computed } from 'vue';
import { useMutation } from '@vue/apollo-composable';
import { ComputeTaskUpdateDocument, ComputeTaskType } from '@/gql/graphql';
import { BAlert, BButton, BModal, BSpinner } from 'bootstrap-vue-next';
import { COMPUTE_TASK_STATUS } from '@/constants';

// --- Component properties ---
const props = defineProps<{
  show: boolean;
  task: ComputeTaskType;
}>();

// --- Component emits ---
const emits = defineEmits(['close', 'closeAndRefresh']);

// --- Component state ---
const show = computed<boolean>(() => {
  return props.show;
});

// --- Mutation: Rename Data Object ---
const mutationInProgress = ref<boolean>(false);
const mutationError = ref<string | null>(null);
const mutationShowSuccess = ref<boolean>(false);

const {
  mutate: executeMutation,
  onDone: onMutationDone,
  onError: onMutationError,
} = useMutation(ComputeTaskUpdateDocument);

onMutationDone((res) => {
  mutationInProgress.value = false;
  if (res.data && res.data.computeTaskUpdate) {
    console.debug(
      'ComputeTask abortion request successful:',
      res.data.computeTaskUpdate
    );
    mutationShowSuccess.value = true;
    setTimeout(() => {
      mutationShowSuccess.value = false;
      emits('closeAndRefresh');
    }, 1000);
  } else {
    mutationError.value = 'Task abortion request failed: ' + res.data;
    console.error('Task abortion request failed:', res.data);
  }
});
onMutationError((err) => {
  console.error('Error aborting compute task:', err);
  mutationInProgress.value = false;
  mutationError.value = err.message;
});

// --- Handle component actions ---
function onAbortConfirmed() {
  if (!props.task.id) {
    mutationError.value = 'Task ID for abortion not provided';
    console.error('Task ID for abortion not provided');
    return;
  }

  mutationInProgress.value = true;
  mutationError.value = null;
  executeMutation({
    id: props.task.id,
    status: COMPUTE_TASK_STATUS.ABORTING,
    abortedAt: new Date(),
  });
}

function handleCloseModal() {
  emits('close');
}
</script>
<template>
  <div>
    <!-- MODAL: Abort Compute Task -->
    <b-modal v-model="show" @hide="handleCloseModal">
      <!-- Title -->
      <template #title>
        <font-awesome-icon icon="times" class="me-2" />
        Abort
        <strong>{{ props.task.name }}</strong>
      </template>
      <!-- Content -->
      <div>
        <!-- Abort? Confirmation Message -->
        <p>
          Are you sure you want to abort the task
          <strong>{{ props.task.name }}</strong
          >?
        </p>

        <!-- Error Message -->
        <b-alert
          v-model="mutationError"
          variant="danger"
          dismissible
          class="mt-3"
          @dismissed="mutationError = null"
        >
          {{ mutationError }}
        </b-alert>
      </div>
      <!-- Footer -->
      <template #footer>
        <b-button variant="secondary" @click="handleCloseModal">
          Cancel
        </b-button>
        <b-button
          variant="danger"
          :disabled="mutationInProgress || mutationShowSuccess"
          @click="onAbortConfirmed"
        >
          <span v-if="mutationInProgress">
            Requesting Abortion
            <b-spinner small label=""></b-spinner>
          </span>
          <span v-else-if="mutationShowSuccess">
            Abortion Requested
            <font-awesome-icon :icon="['fa', 'check-double']" class="me-2" />
          </span>
          <span v-else> Abort Task </span>
        </b-button>
      </template>
    </b-modal>
  </div>
</template>
